import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Upload, X } from "lucide-react";
import api from "@/lib/api";

const KINDS = [
  { value: "revision", label: "Revision needed" },
  { value: "rescheduling", label: "Reschedule" },
  { value: "deadline_extension", label: "Deadline extension" },
  { value: "expense_adjustment", label: "Expense adjustment" },
  { value: "partial_refund", label: "Partial refund" },
  { value: "full_refund", label: "Full refund" },
  { value: "cancellation", label: "Cancellation" },
  { value: "no_show", label: "No-show" },
  { value: "property_damage", label: "Property damage" },
  { value: "unsafe_behavior", label: "Unsafe behavior" },
  { value: "fraud", label: "Fraud / misrepresentation" },
  { value: "prohibited_service", label: "Prohibited service" },
  { value: "request_information", label: "Other — request info" },
];

/**
 * Modal-style dispute opener. Uploads evidence to /files/upload (kind=dispute)
 * then posts to /api/disputes.
 */
export default function OpenDisputeDialog({ open, onClose, transaction, onOpened }) {
  const [kind, setKind] = useState("revision");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [desiredResolution, setDesiredResolution] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "dispute");
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: data.id, name: data.original_filename, url: data.url });
      } catch (err) {
        toast.error(err?.response?.data?.error || `Could not upload ${f.name}`);
      }
    }
    setEvidence((prev) => [...prev, ...added]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error("A subject and description are required.");
      return;
    }
    setSubmitting(true);
    try {
      const isOrder = transaction?.target_collection === "orders" || !!transaction?.order_id;
      const idField = isOrder ? "order_id" : "booking_id";
      const targetId = transaction?.id || transaction?.target_id;
      const payload = {
        [idField]: targetId,
        kind,
        subject: subject.trim(),
        description: description.trim(),
        desired_resolution: desiredResolution || null,
        desired_refund_amount: refundAmount ? Number(refundAmount) : null,
        evidence_media_ids: evidence.map((e) => e.id),
      };
      const { data } = await api.post("/disputes", payload);
      toast.success("Dispute opened — a moderator will review shortly.");
      onOpened?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not open dispute");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 backdrop-blur p-4" data-testid="open-dispute-dialog">
      <div className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-start gap-3 p-6 border-b border-slate-100">
          <div className="h-10 w-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold text-[#071b22]">Open a dispute</div>
            <div className="text-xs text-[#252A34]/70 mt-0.5">The provider's payout is frozen in reserve while we investigate. Only open a dispute if messaging didn't resolve the issue.</div>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-slate-100" aria-label="Close" data-testid="open-dispute-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs text-[#252A34]/70">Type</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="dispute-kind">
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#252A34]/70">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={120}
              placeholder="One-line summary"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              data-testid="dispute-subject"
            />
          </div>
          <div>
            <label className="text-xs text-[#252A34]/70">Describe what happened</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Facts, dates, and what you've tried so far."
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              data-testid="dispute-description"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs text-[#252A34]/70">Desired outcome</label>
              <input
                value={desiredResolution}
                onChange={(e) => setDesiredResolution(e.target.value)}
                placeholder="Redo the work / partial refund…"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="dispute-desired-resolution"
              />
            </div>
            <div>
              <label className="text-xs text-[#252A34]/70">Refund amount (optional)</label>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="$0.00"
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="dispute-refund-amount"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#252A34]/70">Evidence (photos, screenshots, PDFs)</label>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={pick} data-testid="dispute-evidence-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="mt-1 inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium hover:bg-slate-50"
              data-testid="dispute-evidence-picker"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Attach evidence"}
            </button>
            {evidence.length > 0 && (
              <ul className="mt-3 space-y-1">
                {evidence.map((e) => (
                  <li key={e.id} className="text-xs text-[#252A34] flex items-center gap-2">
                    <span className="truncate">{e.name}</span>
                    <button onClick={() => setEvidence((p) => p.filter((x) => x.id !== e.id))} className="text-rose-600 text-xs" data-testid={`dispute-evidence-remove-${e.id}`}>remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-rose-600 text-white px-5 py-2 text-sm font-medium hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="dispute-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Open dispute
          </button>
        </div>
      </div>
    </div>
  );
}
